import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Package, Box, MapPin, Truck, RefreshCw, Search, Filter } from 'lucide-react';

const DeliveryBoxDashboard = () => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupedBoxes, setGroupedBoxes] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    inUse: 0,
    loaded: 0
  });

  useEffect(() => {
    loadBoxes();
    const interval = setInterval(loadBoxes, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterAndGroupBoxes();
  }, [boxes, searchTerm, zoneFilter, statusFilter]);

  const loadBoxes = async () => {
    setLoading(true);
    
    try {
      // First get all delivery boxes
      const { data: boxesData, error: boxesError } = await supabase
        .from('delivery_boxes')
        .select('*')
        .order('box_number', { ascending: true });

      if (boxesError) {
        console.error('Boxes query error:', boxesError);
        setLoading(false);
        return;
      }

      console.log('Boxes loaded:', boxesData?.length);

      if (!boxesData || boxesData.length === 0) {
        setBoxes([]);
        calculateStats([]);
        setLoading(false);
        return;
      }

      // Get all box allocations separately
      const { data: allocationsData, error: allocationsError } = await supabase
        .from('box_allocations')
        .select(`
          id,
          box_id,
          order_id,
          allocated_at,
          removed_at
        `)
        .in('box_id', boxesData.map(b => b.id));

      console.log('Allocations loaded:', allocationsData?.length);

      // Get orders for active allocations
      const activeAllocations = allocationsData?.filter(a => !a.removed_at) || [];
      const orderIds = [...new Set(activeAllocations.map(a => a.order_id))];
      
      let ordersMap = {};
      if (orderIds.length > 0) {
        const { data: ordersData } = await supabase
          .from('orders')
          .select(`
            id,
            order_number,
            status,
            total,
            user_id,
            delivery_address_id
          `)
          .in('id', orderIds)
          .not('status', 'in', '(delivered,delivery_failed)');

        console.log('Orders loaded:', ordersData?.length);

        // Get user profiles
        const userIds = [...new Set(ordersData?.map(o => o.user_id).filter(Boolean) || [])];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, name')
          .in('id', userIds);

        const profilesMap = {};
        profiles?.forEach(p => profilesMap[p.id] = p);

        // Get addresses
        const addressIds = [...new Set(ordersData?.map(o => o.delivery_address_id).filter(Boolean) || [])];
        const { data: addresses } = await supabase
          .from('addresses')
          .select('id, area, physical_address')
          .in('id', addressIds);

        const addressesMap = {};
        addresses?.forEach(a => addressesMap[a.id] = a);

        // Build orders map with related data
        ordersData?.forEach(order => {
          ordersMap[order.id] = {
            ...order,
            user_profiles: profilesMap[order.user_id],
            addresses: addressesMap[order.delivery_address_id]
          };
        });
      }

      // Combine everything
      const processedBoxes = boxesData.map(box => {
        const boxAllocations = activeAllocations
          .filter(a => a.box_id === box.id)
          .map(alloc => ({
            ...alloc,
            orders: ordersMap[alloc.order_id]
          }))
          .filter(alloc => alloc.orders); // Only keep allocations with valid orders

        return {
          ...box,
          activeAllocations: boxAllocations
        };
      });

      console.log('Processed boxes:', processedBoxes.length);
      setBoxes(processedBoxes);
      calculateStats(processedBoxes);
    } catch (error) {
      console.error('Error loading boxes:', error);
    }
    
    setLoading(false);
  };

  const calculateStats = (boxData) => {
    const stats = {
      total: boxData.length,
      available: boxData.filter(b => b.status === 'available').length,
      inUse: boxData.filter(b => b.status === 'in_use').length,
      loaded: boxData.filter(b => b.status === 'loaded').length
    };
    setStats(stats);
  };

  const filterAndGroupBoxes = () => {
    let filtered = [...boxes];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(box =>
        box.box_number.toString().includes(searchTerm) ||
        box.current_zone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        box.activeAllocations.some(alloc => 
          alloc.orders?.order_number?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Zone filter
    if (zoneFilter !== 'all') {
      filtered = filtered.filter(box => box.current_zone === zoneFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(box => box.status === statusFilter);
    }

    // Group by zone
    const grouped = filtered.reduce((acc, box) => {
      const zone = box.current_zone || 'Unassigned';
      if (!acc[zone]) {
        acc[zone] = [];
      }
      acc[zone].push(box);
      return acc;
    }, {});

    setGroupedBoxes(grouped);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-300';
      case 'in_use': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'loaded': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'maintenance': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getZoneColor = (zone) => {
    const colors = {
      'Laudium': 'bg-blue-500',
      'Erasmia': 'bg-green-500',
      'Wierda Park': 'bg-orange-500',
      'Eldoraigne': 'bg-yellow-500',
      'Centurion': 'bg-purple-500',
      'vehicle': 'bg-indigo-500',
      'Unassigned': 'bg-gray-400'
    };
    return colors[zone] || 'bg-gray-500';
  };

  const getUniqueZones = () => {
    const zones = [...new Set(boxes.map(b => b.current_zone).filter(Boolean))];
    return zones.sort();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Delivery Box Dashboard</h1>
              <p className="text-sm text-gray-600">Monitor all delivery boxes organized by zones</p>
            </div>
            
            <button
              onClick={loadBoxes}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Boxes</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Available</div>
              <div className="text-2xl font-bold text-green-600">{stats.available}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">In Use</div>
              <div className="text-2xl font-bold text-blue-600">{stats.inUse}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Loaded</div>
              <div className="text-2xl font-bold text-purple-600">{stats.loaded}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="px-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search boxes, zones, orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Zone Filter */}
            <select
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Zones</option>
              {getUniqueZones().map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="in_use">In Use</option>
              <option value="loaded">Loaded</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Boxes Grouped by Zone */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={32} className="animate-spin text-blue-600" />
          </div>
        ) : Object.keys(groupedBoxes).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Package size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No boxes found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedBoxes)
              .sort(([zoneA], [zoneB]) => zoneA.localeCompare(zoneB))
              .map(([zone, zoneBoxes]) => (
                <div key={zone} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Zone Header */}
                  <div className={`${getZoneColor(zone)} px-6 py-3 border-b flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <MapPin size={20} className="text-white" />
                      <h2 className="text-lg font-bold text-white">{zone}</h2>
                      <span className="bg-white bg-opacity-30 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {zoneBoxes.length} {zoneBoxes.length === 1 ? 'box' : 'boxes'}
                      </span>
                    </div>
                  </div>

                  {/* Boxes Grid */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {zoneBoxes.map(box => (
                        <div key={box.id} className={`border-2 rounded-lg p-4 ${getStatusColor(box.status)}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Box size={20} className="text-current" />
                              <span className="font-bold text-lg">Box {box.box_number}</span>
                            </div>
                            <span className="px-2 py-1 bg-white rounded-full text-xs font-medium">
                              {box.status}
                            </span>
                          </div>

                          {box.activeAllocations && box.activeAllocations.length > 0 ? (
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                Orders ({box.activeAllocations.length})
                              </div>
                              {box.activeAllocations.slice(0, 3).map(allocation => (
                                <div key={allocation.id} className="bg-white rounded p-2 text-xs">
                                  <div className="font-medium text-gray-900">
                                    {allocation.orders?.order_number}
                                  </div>
                                  <div className="text-gray-600">
                                    {allocation.orders?.user_profiles?.name || 'Unknown'}
                                  </div>
                                  <div className="text-gray-500 text-xs">
                                    {allocation.orders?.addresses?.area}
                                  </div>
                                  <div className="text-purple-600 font-medium mt-1">
                                    R{parseFloat(allocation.orders?.total || 0).toFixed(2)}
                                  </div>
                                  <div className="mt-1">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                      allocation.orders?.status === 'ready_for_dispatch' ? 'bg-green-100 text-green-800' :
                                      allocation.orders?.status === 'out_for_delivery' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {allocation.orders?.status?.replace(/_/g, ' ')}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {box.activeAllocations.length > 3 && (
                                <div className="text-xs text-gray-600 italic text-center">
                                  +{box.activeAllocations.length - 3} more orders
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <Package size={24} className="mx-auto text-gray-400 mb-2" />
                              <div className="text-xs text-gray-500">No active orders</div>
                            </div>
                          )}

                          {box.last_used_at && (
                            <div className="mt-3 pt-3 border-t border-current border-opacity-20">
                              <div className="text-xs text-gray-600">
                                Last used: {new Date(box.last_used_at).toLocaleDateString()}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryBoxDashboard;
